import { CaseConverter } from '../helpers/case-converter.helper';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { HttpAuthHelperService } from './http-auth-helper.service';
import { Survey } from '../entities/survey.entity';

@Injectable({
  providedIn: 'root',
})
export class AdminSurveyService {
  private baseUrl = environment.endpoint;

  constructor(private httpAuthHelperService: HttpAuthHelperService) { }

  async save(survey: Survey): Promise<any> {
    return survey.uuid === '-1' ? this.create(survey) : this.update(survey);
  }

  async create(survey: Survey): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.httpAuthHelperService
        .requestPromise(
          'POST',
          `${this.baseUrl}/survey/create-survey`,
          CaseConverter.keysToSnakeCase(survey)
        )
        .then((data) => {
          resolve(data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  async update(survey: Survey): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.httpAuthHelperService
        .requestPromise(
          'POST',
          `${this.baseUrl}/survey/update-survey`,
          CaseConverter.keysToSnakeCase(survey)
        )
        .then((data) => {
          resolve(data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  async updateStatus(survey: Survey): Promise<any> {
    const payload = {
      uuid: survey.uuid,
      isDisabled: survey.isDisabled,
    };
    return new Promise<any>((resolve, reject) => {
      this.httpAuthHelperService
        .requestPromise(
          'POST',
          `${this.baseUrl}/survey/update-status-survey`,
          payload
        )
        .then((data) => {
          resolve(data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  async getSurveyAssets(keys: string[]): Promise<any> {
    const validKeys = Array.isArray(keys)
      ? keys.filter((key) => key !== undefined)
      : [];

    const processedKeys = validKeys.map((key) =>
      key.endsWith('.png') ? key : `${key}.png`
    );

    const payload = {
      keys: processedKeys,
    };
    return new Promise<any>((resolve, reject) => {
      this.httpAuthHelperService
        .requestPromise(
          'POST',
          `${this.baseUrl}/survey/get-survey-assets`,
          payload
        )
        .then((data) => {
          if (data.body?.response) {
            const processedResponse: any = {};
            Object.keys(data.body.response).forEach((key) => {
              const newKey = key.replace('.png', '');
              processedResponse[newKey] = data.body.response[key];
            });
            data.body.response = processedResponse;
          }
          resolve(data.body);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  async uploadAsset(
    key: string,
    buffer: any,
    onProgress: (progress: number) => void
  ): Promise<any> {
    const payload = {
      key,
      buffer,
    };
    return new Promise<any>((resolve, reject) => {
      this.httpAuthHelperService
        .requestWithProgressPromise(
          'POST',
          `${this.baseUrl}/survey/create-survey-assets`,
          onProgress,
          payload
        )
        .then((data) => {
          resolve(data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  getAll(project: string): Promise<Survey[]> {
    if (navigator.onLine) {
      return this.get(project);
    } else {
      return Promise.resolve([]);
    }
  }

  async HARD_DELETE_SurveyHistory(
    uuid: string,
    parentUUID: any,
    onProgress: (progress: number) => void
  ): Promise<any> {
    const payload = {
      uuid,
      parent_uuid: parentUUID,
    };
    return new Promise<any>((resolve, reject) => {
      this.httpAuthHelperService
        .requestWithProgressPromise(
          'POST',
          `${this.baseUrl}/survey/delete-survey-history`,
          onProgress,
          payload
        )
        .then((data) => {
          resolve(data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  private get(project: string): Promise<Survey[]> {
    return new Promise<Survey[]>((resolve, reject) => {
      this.httpAuthHelperService
        .requestPromise('POST', `${this.baseUrl}/survey/get-surveys`, {
          project: project,
        })
        .then((data) => {
          if (data?.body?.response?.length > 0) {
            const surveys = CaseConverter.keysToCamelCase(data.body.response);
            resolve(surveys as Survey[]);
          } else {
            resolve([]);
          }
        })
        .catch((err) => {
          reject(err);
        });
    });
  }
}
